import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

/** Settings checkbox. Checked means product emails are on. */
const EmailNotificationToggle: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setEnabled(user.notificationPreferences?.emailNotificationsEnabled !== false);
  }, [user]);

  const handleToggle = async (next: boolean) => {
    if (!user?.id) return;
    setError('');
    setEnabled(next);
    let saved = false;
    try {
      setBusy(true);
      const updateUser = httpsCallable(functions, 'updateUser');
      const result = await updateUser({
        userId: user.id,
        updates: {
          'notificationPreferences.emailNotificationsEnabled': next,
        },
      });
      const data = result.data as { success?: boolean; error?: string };
      if (data?.success === false) {
        throw new Error(data.error || 'Failed to save email notifications.');
      }
      saved = true;
    } catch (err: unknown) {
      setEnabled(!next);
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to save email notifications.';
      setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setBusy(false);
    }
    if (saved) {
      try {
        await refreshUser();
      } catch {
        // The choice is already stored. A failed reload must not turn the box back on.
      }
    }
  };

  return (
    <div className="mb-4">
      <label className="flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => handleToggle(e.target.checked)}
          className="sr-only"
          disabled={busy}
        />
        <div
          className="w-6 h-6 border-2 rounded flex items-center justify-center transition-all mr-3 flex-shrink-0"
          style={{
            borderColor: '#92d050',
            backgroundColor: enabled ? '#92d050' : 'transparent',
          }}
        >
          {enabled && (
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
        <span className="text-white text-sm sm:text-base">Email notifications</span>
      </label>
      {!enabled ? (
        <p className="mt-1 ml-9 text-sm text-red-500">
          {user?.userType === 'investor'
            ? 'You won\u2019t know when a startup matching your investment preferences join PRAIRI or a founder replies to you.'
            : 'You won\u2019t know when an investor messages you'}
        </p>
      ) : null}
      {error ? <p className="mt-2 ml-9 text-sm text-red-400">{error}</p> : null}
    </div>
  );
};

export default EmailNotificationToggle;
